import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Redirect, useHistory } from 'react-router-dom';
import DataTypes from '../components/Table/dataTypes';
import Table from '../components/Table/Table';
import { EnrichmentData, States } from '../features/PpsiContainer/constants';
import useViewVisited from '../features/app/hooks/useViewVisted';
import View from './views';
import PaperContainer from '../components/common/PaperContainer';
import {
  fetchRecalledErrorForOne,
  fetchReviewPpsis,
  hasRecalledPPSIError
} from 'features/PpsiContainer/actions';
import { toJS } from '../utils/immutableUtils';
import AppBar from '@material-ui/core/AppBar';
import MUIToolbar from '@material-ui/core/Toolbar';
import GoBackButton from 'components/Form/Toolbar/GoBackButton';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import { globalWaitingReviewRoute, globalReviewApprovalRoute } from 'routes';
import {
  getHasRecalledError,
  getIsFetching,
  getReviewPpsis
} from 'features/PpsiContainer/selectors';
import ProgressFullscreen from 'components/common/ProgressFullscreen';
import {
  getMarketLanguage,
  getReviewMarket,
  getMarketConfig,
  fetchingMarketConfig,
  marketConfigFetchError
} from 'features/app/selectors';
import { getMarketConfigs } from 'features/app/actions';
import { makeStyles } from '@material-ui/core/styles';

import {
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@material-ui/core';
import MessageIcon from '@material-ui/icons/Message';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles(theme => ({
  appBar: {
    position: 'relative', // To make z-index work
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.text.primary,
    background: lighten(theme.palette.primary.light, 0.97)
  },
  flex: {
    flexGrow: 1
  },
  tooltip: {
    maxWidth: 450,
    backgroundColor: '#000'
  }
}));

const WaitingForGlobalReview = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const isFetching = useSelector(getIsFetching);
  const reviewPpsi = useSelector(getReviewPpsis);
  const hasRecalledError = useSelector(getHasRecalledError);
  const reviewPpsis = toJS(reviewPpsi, []);
  const reviewMarketLanguage = useSelector(getMarketLanguage);
  const reviewMarket = useSelector(getReviewMarket);
  const isFetchingConfig = useSelector(fetchingMarketConfig);
  const configFetchError = useSelector(marketConfigFetchError);
  // const marketConfig = useSelector(getMarketConfig).toJS();
  const filter = useMemo(() => ({ enrichmentStatus: States.GLOBALREVIEW }), []);
  const dispatch = useDispatch();
  const marketCode = id;
  const { isViewVisited } = useViewVisited(
    View.ReviewWaitingMarketSummary.name,
    isFetching
  );

  localStorage.setItem('localMarket', JSON.stringify(id));
  localStorage.setItem('marketLanguage', JSON.stringify(reviewMarketLanguage));

  const isFetchingDone = isFetchingConfig || isFetching;

  const isFetched = isViewVisited ? true : !isFetchingDone;

  const GlobalReviewColumns = {
    IfoodID: {
      name: 'formulaCode',
      label: 'IFood ID',
      dataField: 'formulaCode',
      //   filterable: true,
      sortable: true,
      width: 120,
      grow: 0,
      shrink: 0
    },
    PresentationName: {
      name: 'presentationName',
      // dataField: 'itemCode',
      calculateCellValue: rowData =>
        rowData.inProgressPpsiEnrichmentData.info[reviewMarketLanguage]
          .presentationName,
      //   filterable: true,
      style: rowData => ({
        color: '#0058A3',
        cursor: 'pointer'
      }),
      sortable: true,
      label: 'Presentation Name',
      width: 300,
      grow: 0,
      shrink: 0,
      funCall: true
      // link: rowData => {
      //   return globalReviewApprovalRoute(rowData.id);
      // }
    },
    PIANumber: {
      name: 'piaArticleNumber',
      dataField: 'piaArticleNumber',
      sortable: false,
      label: 'PIA Number',
      width: 120,
      grow: 0,
      shrink: 0
    },
    MarketCode: {
      name: 'market',
      dataField: 'market',
      sortable: false,
      label: 'Market',
      width: 80,
      grow: 0,
      shrink: 0
    },
    Versions: {
      name: 'formulaVersion',
      dataField: 'inProgressPpsiEnrichmentData.version',
      sortable: false,
      label: 'Version',
      searchable: false,
      width: 80,
      grow: 0,
      shrink: 0
    },
    SendForReviewDate: {
      name: 'sendReviewDate',
      dataField: 'inProgressPpsiEnrichmentData.sendReviewDate',
      //   filterable: true,
      sortable: true,
      dataType: DataTypes.Date,
      label: 'Review Sent Date',
      searchable: false,
      width: 150,
      grow: 0,
      shrink: 0
    },
    Comments: {
      name: 'comments',
      // dataField: 'inProgressPpsiEnrichmentData.globalReviewComment',
      sortable: false,
      label: 'Comments',
      searchable: false,
      calculateCellValue: rowData => {
        const comment =
          rowData.inProgressPpsiEnrichmentData.globalReviewComment || '';
        return (
          <>
            {comment.length > 50 ? (
              <>
                <Typography variant="body2">
                  {comment.slice(0, 25)}...
                </Typography>
                <Tooltip
                  classes={{ tooltip: classes.tooltip }}
                  title={
                    <Typography variant="body2" fontSize={20}>
                      {comment}...
                    </Typography>
                  }
                >
                  <IconButton color="action" size="medium">
                    <MessageIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              comment
            )}
          </>
        );
      }
      // width: 180,
      // grow: 0,
      // shrink: 0
    },
    EmptySpace: {
      name: 'itemCode',
      dataField: 'itemCode',
      label: '  '
    }
  };

  useEffect(() => {
    dispatch(getMarketConfigs(id));
    dispatch(fetchReviewPpsis(filter, marketCode));
    // dispatch(fetchMarketUtensils(id))
    // dispatch(fetchMarketIngredients(marketCode));
  }, []);

  if (configFetchError) {
    throw configFetchError;
  }

  if (isFetchingConfig || isFetching) {
    return <ProgressFullscreen />;
  }

  if (reviewPpsis.length === 0 && !(isFetchingConfig && isFetching)) {
    return <Redirect to={globalWaitingReviewRoute()} />;
  }

  View.ReviewWaitingMarketSummary.visit();

  return (
    <>
      <AppBar
        position="static"
        classes={{ root: classes.appBar }}
        elevation={1}
      >
        <MUIToolbar>
          <GoBackButton />
        </MUIToolbar>
      </AppBar>
      <PaperContainer showProgress={!isFetched}>
        {reviewMarketLanguage && (
          <Table
            title={`Waiting for review in ${id} market`}
            // title="updatesList.updatesTableHeader"
            data={reviewPpsis}
            columns={[
              GlobalReviewColumns.IfoodID,
              GlobalReviewColumns.PresentationName,
              GlobalReviewColumns.PIANumber,
              GlobalReviewColumns.MarketCode,
              GlobalReviewColumns.Versions,
              GlobalReviewColumns.SendForReviewDate,
              GlobalReviewColumns.Comments
              //   GlobalReviewColumns.EmptySpace
            ]}
            getObjectId={object => object.id}
            defaultSorting={[
              { columnName: 'presentationName', direction: 'asc' }
            ]}
            // showLanguageSelector={true}
            marketLanguage={reviewMarket === id}
            selectable
          />
        )}
      </PaperContainer>
      <Dialog open={hasRecalledError}>
        <DialogContent>
          <DialogContentText component="div">
            Sorry, but you can't Review this PPSI. <br />
            PPSI is recalled and edited by Market Admin.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            key="stay"
            onClick={() => {
              // globalMarketReviewRoute(localMarket)
              if (reviewPpsis.length === 1) {
                dispatch(hasRecalledPPSIError(false));
                history.push(globalWaitingReviewRoute());
              } else {
                dispatch(fetchReviewPpsis(filter, marketCode));
                dispatch(hasRecalledPPSIError(false));
              }
            }}
            color="primary"
            style={{ marginRight: '10px' }}
          >
            BACK TO THE LIST
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WaitingForGlobalReview;
